import { css, StyleSheet } from 'aphrodite';
import { type ChangeEvent, type FC, useMemo, useRef, useState } from 'react';

import {
	FormFieldType,
	getContentHtmlStringValueFromRawRichTextContentState,
	type IContact,
	type IRichContentEditorState,
	type IUser,
} from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ToolbarLight, createRichContentEditorStateWithText } from '../../../models/UiUtils';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemCheckboxIndicator,
	ComboboxList,
	ComboboxValue,
	useComboboxAnchor,
} from '../../../web/components-v2/Combobox';
import { ComboboxAvatarItem } from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../web/components/UserCombobox';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import { useContactSearchQuery } from '../../../queries/Contact/useContactSearchQuery';
import type { DealFieldDef } from './types';
import { useDealFieldOptions } from './useDealFieldOptions';
import { useScheduleDemoMutation } from './useScheduleDemoMutation';

/**
 * Props for the pure `DealField` renderer.
 *
 * The renderer is intentionally controlled-only: callers own the `value` and
 * react to `onChange`. There is no internal state for the value, so the
 * component can be driven equally well by `useState` in `DealForm` or by a
 * future form library (react-hook-form, etc.) without changes to the
 * renderer.
 *
 * `companyId` and `associatedContactId` are only consumed by the
 * `MeetingScheduler` branch — the demo-link endpoint is keyed by the lead
 * (`lead/{companyId}/meetingScheduler`) and requires an associated contact.
 */
export type DealFieldProps = {
	field: DealFieldDef;
	value: unknown;
	onChange: (next: unknown) => void;
	error?: string;
	disabled?: boolean;
	/** Lead/company id — required for the MeetingScheduler field type. */
	companyId?: string;
	/** Associated contact id — required for the MeetingScheduler field type. */
	associatedContactId?: string;
	/**
	 * Fires when the rep successfully clicks "Find Demo Spot" and the booking
	 * URL resolves. The form uses this to hide the demo-perform-date and
	 * demo-performer fields (the server populates them on rep return).
	 */
	onSchedulerEngaged?: () => void;
	/** Fires when the rep flips back to manual demo-time entry. */
	onSchedulerDisengaged?: () => void;
};

/**
 * Pure renderer for a single field of the Create-Deal form.
 *
 * Branches on `field.fieldType` and renders the appropriate components-v2
 * primitive (Input, Select, Checkbox, Combobox, DateInput, etc.) plus a
 * label and optional error message. Unsupported field types render a
 * placeholder; see the deal README for the v1 coverage matrix and
 * follow-up list.
 */
export const DealField: FC<DealFieldProps> = ({
	field,
	value,
	onChange,
	error,
	disabled,
	companyId,
	associatedContactId,
	onSchedulerEngaged,
	onSchedulerDisengaged,
}) => {
	if (field.isHidden) return null;

	const required = field.isOptional !== true;

	// MeetingScheduler renders its own controls without the standard label —
	// the legacy form also hides the label for this field type. The wrapper
	// is still rendered so error messages can attach.
	const showLabel = field.fieldType !== FormFieldType.MeetingScheduler;

	// Stable id used by `DealForm.handleCreate` to scroll the first
	// validation error into view (matches legacy
	// `DealDrawerContent.onActionClick` scroll-to-error behaviour, but keyed
	// off the schema `name` rather than the legacy `id+label+fieldType`
	// concatenation).
	const rootId = `deal-field-${field.name}`;

	return (
		<div id={rootId} className={css(styles.root)}>
			{showLabel ? (
				<label className={css(styles.label)}>
					<span className={css(styles.labelText)}>
						{field.label}
						{required && (
							<span aria-hidden='true' className={css(styles.requiredAsterisk)}>
								*
							</span>
						)}
					</span>
					<FieldBody
						field={field}
						value={value}
						onChange={onChange}
						disabled={disabled}
						companyId={companyId}
						associatedContactId={associatedContactId}
						onSchedulerEngaged={onSchedulerEngaged}
						onSchedulerDisengaged={onSchedulerDisengaged}
					/>
				</label>
			) : (
				<FieldBody
					field={field}
					value={value}
					onChange={onChange}
					disabled={disabled}
					companyId={companyId}
					associatedContactId={associatedContactId}
					onSchedulerEngaged={onSchedulerEngaged}
					onSchedulerDisengaged={onSchedulerDisengaged}
				/>
			)}
			{error && <div className={css(styles.error)}>{error}</div>}
		</div>
	);
};

/**
 * Stringify the controlled value for primitives that expect a string. We
 * prefer the explicit `string` branch first so we don't accidentally call
 * `String(undefined)` → `'undefined'` for empty inputs.
 */
function asStringValue(value: unknown): string {
	if (typeof value === 'string') return value;
	if (value == null) return '';
	return String(value);
}

type FieldBodyProps = {
	field: DealFieldDef;
	value: unknown;
	onChange: (next: unknown) => void;
	disabled?: boolean;
	companyId?: string;
	associatedContactId?: string;
	onSchedulerEngaged?: () => void;
	onSchedulerDisengaged?: () => void;
};

/**
 * Renders the actual input control for the field. Split out so the
 * `MeetingScheduler` branch (which manages its own internal toggle state)
 * can mount hooks alongside the rest of the field types.
 */
const FieldBody: FC<FieldBodyProps> = ({
	field,
	value,
	onChange,
	disabled,
	companyId,
	associatedContactId,
	onSchedulerEngaged,
	onSchedulerDisengaged,
}) => {
	const stringValue = asStringValue(value);

	switch (field.fieldType) {
		case FormFieldType.String:
		case FormFieldType.Email:
			return (
				<Input
					value={stringValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
					placeholder={field.placeHolder}
					disabled={disabled}
				/>
			);

		case FormFieldType.Integer:
		case FormFieldType.Decimal:
		case FormFieldType.Money:
			return (
				<Input
					value={stringValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
					placeholder={field.placeHolder}
					inputMode='numeric'
					disabled={disabled}
				/>
			);

		case FormFieldType.Text:
		case FormFieldType.Note:
			return <RichTextFieldBody value={value} onChange={onChange} />;

		case FormFieldType.Option:
			return (
				<OptionFieldBody
					field={field}
					value={stringValue}
					onChange={onChange}
					disabled={disabled}
					companyId={companyId}
				/>
			);

		case FormFieldType.MultipleOptions:
			return <MultipleOptionsFieldBody field={field} value={value} onChange={onChange} companyId={companyId} />;

		case FormFieldType.Boolean:
			return (
				<Checkbox
					checked={value === true}
					onCheckedChange={checked => onChange(checked === true)}
					disabled={disabled}
				/>
			);

		case FormFieldType.DateTime:
			return <DateTimeFieldBody value={stringValue} onChange={onChange} disabled={disabled} />;

		case FormFieldType.Date:
			return <DateInput value={stringValue} onChange={next => onChange(next)} />;

		case FormFieldType.User:
			return <UserFieldBody value={stringValue} onChange={onChange} placeholder={field.placeHolder} />;

		case FormFieldType.Contact:
			return <ContactFieldBody value={stringValue} onChange={onChange} placeholder={field.placeHolder} />;

		case FormFieldType.MeetingScheduler:
			return (
				<MeetingSchedulerFieldBody
					value={stringValue}
					onChange={onChange}
					companyId={companyId}
					associatedContactId={associatedContactId}
					onSchedulerEngaged={onSchedulerEngaged}
					onSchedulerDisengaged={onSchedulerDisengaged}
				/>
			);

		default:
			// FullAddress / FileUpload / Custom / Time / Name remain deferred —
			// render a clear placeholder so the rep can still submit (server
			// defaults will fill these for v1).
			return <div className={css(styles.unsupported)}>Field type not supported in v1: {field.fieldType}</div>;
	}
};

/* -------------------------------------------------------------------------- */
/*  Rich-text body                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Renders the legacy-parity rich-text editor for `Text` and `Note` field
 * types. Initialises content state from the (possibly HTML) string value
 * and reports changes back as an HTML string — matching what the legacy
 * `CustomFormViewModel.save` path POSTs.
 */
const RichTextFieldBody: FC<{ value: unknown; onChange: (next: unknown) => void }> = ({ value, onChange }) => {
	// Initialise once from the incoming string — `RichContentDocumentEditor`
	// is uncontrolled internally, so re-deriving on every render would reset
	// the cursor. The initial state is captured in a ref-like memo keyed on
	// the initial value, not the live value.
	const initialState = useMemo(
		() => createRichContentEditorStateWithText(asStringValue(value)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return (
		<RichContentDocumentEditor
			autoFocus={false}
			className={css(styles.richEditor)}
			config={{
				toolbar: ToolbarLight,
				minHeight: 120,
				maxHeight: 280,
			}}
			contentState={initialState}
			onContentStateChanged={(state: IRichContentEditorState) => {
				onChange(getContentHtmlStringValueFromRawRichTextContentState(state.getRawRichTextContent()));
			}}
		/>
	);
};

/* -------------------------------------------------------------------------- */
/*  Date + time composite                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Splits the controlled ISO-ish string into its date + time halves so the
 * rep can edit each independently. On any change, recomposes a full ISO-8601
 * UTC string (the legacy contract for `DateTime` fields).
 *
 * Timezone defaults to the browser's; a follow-up may surface an explicit
 * `TimezonePicker` for parity with the legacy `AidaDayPicker`.
 */
const DateTimeFieldBody: FC<{ value: string; onChange: (next: unknown) => void; disabled?: boolean }> = ({
	value,
	onChange,
	disabled,
}) => {
	const browserTimeZone = useMemo(() => {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone;
		} catch {
			return undefined;
		}
	}, []);

	const { date, time } = useMemo(() => splitIsoDateTime(value), [value]);

	const emit = (nextDate: string, nextTime: string) => {
		if (!nextDate) {
			onChange('');
			return;
		}
		const t = nextTime || '00:00';
		// Construct in local time then serialize to ISO-8601 UTC; this matches
		// what the legacy `selectedDate.format()` (a moment instance) emitted
		// once consumed by JSON.stringify on the server side.
		const composed = new Date(`${nextDate}T${t}:00`);
		if (Number.isNaN(composed.getTime())) {
			onChange(`${nextDate}T${t}`);
			return;
		}
		onChange(composed.toISOString());
	};

	return (
		<div className={css(styles.dateTimeWrap)}>
			<div className={css(styles.dateTimeRow)}>
				<div className={css(styles.dateCol)}>
					<DateInput value={date} onChange={next => emit(next, time)} />
				</div>
				<div className={css(styles.timeCol)}>
					<Input
						type='time'
						value={time}
						onChange={(e: ChangeEvent<HTMLInputElement>) => emit(date, e.target.value)}
						disabled={disabled}
					/>
				</div>
			</div>
			{browserTimeZone ? <span className={css(styles.tzHint)}>{browserTimeZone}</span> : null}
		</div>
	);
};

/**
 * Best-effort parse of the controlled value into `YYYY-MM-DD` + `HH:MM`.
 * Accepts both the legacy `datetime-local`-style string and a full ISO
 * timestamp.
 */
function splitIsoDateTime(value: string): { date: string; time: string } {
	if (!value) return { date: '', time: '' };
	// `2026-05-06T14:30` (datetime-local) — slice directly.
	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
		return { date: value.slice(0, 10), time: value.slice(11, 16) };
	}
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };
	const pad = (n: number) => String(n).padStart(2, '0');
	const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
	const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
	return { date, time };
}

/* -------------------------------------------------------------------------- */
/*  Option single-select                                                      */
/* -------------------------------------------------------------------------- */

type SimpleOption = { id: string; label: string };

/**
 * Resolve the option list for an `Option` / `MultipleOptions` field.
 *
 * The form schema response (`lead/{companyId}/CreateDeal`) usually
 * returns these field types with `options` *empty*; the choices live
 * behind a per-field paginated endpoint
 * (`lead/{companyId}/CreateDeal/Field/{fieldId}`) which the legacy
 * `FormFieldViewModel.loadOptions` calls during mount. We mirror that
 * here through `useDealFieldOptions`. When the schema *does* include
 * inline options (some custom-form deployments) we use them directly and
 * skip the network round-trip.
 */
function useResolvedFieldOptions(
	field: DealFieldDef,
	companyId: string | undefined
): { options: SimpleOption[]; isLoading: boolean } {
	const inlineOptions = field.options ?? [];
	const hasInline = inlineOptions.length > 0;
	const lazy = useDealFieldOptions(hasInline ? undefined : companyId, hasInline ? undefined : field.id);
	if (hasInline) {
		return { options: inlineOptions, isLoading: false };
	}
	return { options: lazy.options, isLoading: lazy.isLoading };
}

/**
 * Single-select via `Select`. Lazy-loads choices through the per-field
 * options endpoint when the schema doesn't ship them inline. We
 * intentionally pass `value={stringValue}` (an empty string is a
 * perfectly valid controlled value) — passing `undefined` makes Base UI
 * treat the Select as uncontrolled, then flip it to controlled on first
 * pick, which raises a console warning.
 */
const OptionFieldBody: FC<{
	field: DealFieldDef;
	value: string;
	onChange: (next: unknown) => void;
	disabled?: boolean;
	companyId?: string;
}> = ({ field, value, onChange, disabled, companyId }) => {
	const { options, isLoading } = useResolvedFieldOptions(field, companyId);
	const selected = options.find(o => o.id === value);
	const placeholder = isLoading ? 'Loading…' : (field.placeHolder ?? '--');
	return (
		<Select value={value} onValueChange={onChange} disabled={disabled || isLoading}>
			<SelectTrigger>
				<SelectValue placeholder={placeholder}>{selected?.label}</SelectValue>
			</SelectTrigger>
			<SelectContent>
				{options.map(o => (
					<SelectItem key={o.id} value={o.id}>
						{o.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
};

/* -------------------------------------------------------------------------- */
/*  MultipleOptions multi-select                                              */
/* -------------------------------------------------------------------------- */

/**
 * True multi-select via `Combobox` in `multiple` mode + `ComboboxChips`.
 * The controlled value is a comma-joined string of ids — matching the
 * legacy `selectedOptions.map(o => o.dataContext.id).join(',')` contract
 * (see legacy `DealField/index.tsx:259`).
 */
const MultipleOptionsFieldBody: FC<{
	field: DealFieldDef;
	value: unknown;
	onChange: (next: unknown) => void;
	companyId?: string;
}> = ({ field, value, onChange, companyId }) => {
	const { options } = useResolvedFieldOptions(field, companyId);
	const stringValue = asStringValue(value);
	const selectedIds = useMemo(() => stringValue.split(',').filter(Boolean), [stringValue]);
	const selectedItems = useMemo(
		() => selectedIds.map(id => options.find(o => o.id === id)).filter((o): o is SimpleOption => Boolean(o)),
		[selectedIds, options]
	);
	const anchorRef = useComboboxAnchor();

	return (
		<Combobox
			multiple
			items={options}
			value={selectedItems}
			onValueChange={next => {
				const ids = (next as SimpleOption[]).map(o => o.id);
				onChange(ids.join(','));
			}}
			isItemEqualToValue={(a, b) => (a as SimpleOption).id === (b as SimpleOption).id}
			itemToStringValue={item => (item as SimpleOption).id}
			itemToStringLabel={item => (item as SimpleOption).label}
		>
			<ComboboxChips ref={anchorRef}>
				<ComboboxValue>
					{(selected: SimpleOption[]) => (
						<>
							{selected.map(o => (
								<ComboboxChip key={o.id} aria-label={o.label}>
									{o.label}
									<ComboboxChipRemove />
								</ComboboxChip>
							))}
							<ComboboxChipsInput placeholder={selected.length > 0 ? '' : (field.placeHolder ?? 'Select…')} />
						</>
					)}
				</ComboboxValue>
			</ComboboxChips>
			<ComboboxContent anchor={anchorRef}>
				<ComboboxEmpty>No options</ComboboxEmpty>
				<ComboboxList>
					{(option: SimpleOption) => (
						<ComboboxItem key={option.id} value={option}>
							<ComboboxItemCheckboxIndicator />
							{option.label}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

/* -------------------------------------------------------------------------- */
/*  User combobox                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Wraps the pre-built `UserCombobox` (a `Combobox` over
 * `useUserAutocompleteQuery`) and reduces the result to a user id string —
 * which is what `CustomFormViewModel.save` POSTs for `User`-typed fields.
 */
const UserFieldBody: FC<{ value: string; onChange: (next: unknown) => void; placeholder?: string }> = ({
	value,
	onChange,
	placeholder,
}) => {
	return (
		<UserCombobox id={value || undefined} onValueChange={(user: IUser | null) => onChange(user?.id ?? '')}>
			<ComboboxInput placeholder={placeholder ?? 'Search users…'} showClear={!!value} />
			<ComboboxContent>
				<UserComboboxStatus />
				<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
			</ComboboxContent>
		</UserCombobox>
	);
};

/* -------------------------------------------------------------------------- */
/*  Contact combobox                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A from-scratch contact picker built on the components-v2 `Combobox`
 * primitives + `useContactSearchQuery`. Mirrors the shape of `UserCombobox`
 * (avatar, title, email subtitle) but for contacts. We don't currently
 * resolve a pre-existing id back into a contact object (no parallel of
 * `useUserQuery` for the chosen contact) — for v1 the form is a fresh
 * create flow, so we render the most recently selected display name and
 * fall back to the id otherwise.
 */
const ContactFieldBody: FC<{ value: string; onChange: (next: unknown) => void; placeholder?: string }> = ({
	value,
	onChange,
	placeholder,
}) => {
	const [searchValue, setSearchValue] = useState('');
	const lastSelectedRef = useRef<IContact | null>(null);
	const search = useContactSearchQuery({
		searchQuery: searchValue,
		enabled: searchValue.length >= 2,
	});

	const items = search.data?.values ?? [];

	// If the parent has a value but no contact object, surface the id in the
	// input so the rep can see what's selected; once they type, the search
	// query takes over.
	const inputValue = (() => {
		if (searchValue) return searchValue;
		if (lastSelectedRef.current && lastSelectedRef.current.id === value) {
			return getContactDisplayName(lastSelectedRef.current);
		}
		return '';
	})();

	return (
		<Combobox
			items={items}
			value={lastSelectedRef.current && lastSelectedRef.current.id === value ? lastSelectedRef.current : null}
			inputValue={inputValue}
			onInputValueChange={setSearchValue}
			filter={null}
			itemToStringLabel={(item: IContact) => getContactDisplayName(item)}
			isItemEqualToValue={(a, b) => (a as IContact)?.id === (b as IContact)?.id}
			onValueChange={(contact: IContact | null) => {
				lastSelectedRef.current = contact;
				onChange(contact?.id ?? '');
				if (contact) setSearchValue('');
			}}
		>
			<ComboboxInput placeholder={placeholder ?? 'Search contacts…'} showClear={!!value} />
			<ComboboxContent>
				<ComboboxEmpty>{searchValue.length < 2 ? 'Type to search…' : 'No contacts found'}</ComboboxEmpty>
				<ComboboxList>
					{(contact: IContact) => (
						<ComboboxAvatarItem
							key={contact.id}
							value={contact}
							avatarImageUrl={contact.profilePic ?? ''}
							avatarFallbackText={`${contact.firstName?.[0] ?? ''}${contact.lastName?.[0] ?? ''}`.toUpperCase()}
							title={getContactDisplayName(contact)}
							description={contact.emailAddresses?.[0]?.value ?? ''}
						/>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

function getContactDisplayName(contact: IContact): string {
	const first = contact.firstName ?? '';
	const last = contact.lastName ?? '';
	const full = `${first} ${last}`.trim();
	return full || contact.emailAddresses?.[0]?.value || contact.id || '';
}

/* -------------------------------------------------------------------------- */
/*  MeetingScheduler ("Find Demo Spot")                                       */
/* -------------------------------------------------------------------------- */

type SchedulingMode = 'auto' | 'manual';

/**
 * Renders the "Find Demo Spot" / "Enter Demo Time Manually" toggle pair.
 * Auto mode posts to `lead/{companyId}/meetingScheduler` (via
 * `useScheduleDemoMutation`) and opens the returned URL in a new tab.
 *
 * On a successful auto-scheduler click we fire `onSchedulerEngaged`; the
 * parent `DealForm` uses this to hide the demo-perform-date and
 * demo-performer fields (matching legacy `meetingSchedulerState ===
 * Automatic` field-hiding behaviour). Flipping back to manual fires
 * `onSchedulerDisengaged`.
 */
const MeetingSchedulerFieldBody: FC<{
	value: string;
	onChange: (next: unknown) => void;
	companyId?: string;
	associatedContactId?: string;
	onSchedulerEngaged?: () => void;
	onSchedulerDisengaged?: () => void;
}> = ({ value, onChange, companyId, associatedContactId, onSchedulerEngaged, onSchedulerDisengaged }) => {
	const userSession = useUserSession();
	const [mode, setMode] = useState<SchedulingMode>(value ? 'auto' : 'manual');
	const [errorText, setErrorText] = useState<string | null>(null);
	const scheduleDemo = useScheduleDemoMutation();

	const onAutoClick = async () => {
		setErrorText(null);
		if (!companyId) {
			setErrorText('No lead selected — refresh the page and try again.');
			return;
		}
		if (!associatedContactId) {
			setErrorText('You must select a contact to use the demo scheduler.');
			return;
		}
		try {
			const browserTimeZone = (() => {
				try {
					return Intl.DateTimeFormat().resolvedOptions().timeZone;
				} catch {
					return undefined;
				}
			})();
			const userTimeZone = userSession?.user?.userPreferences?.timeZone || browserTimeZone;
			const result = await scheduleDemo.mutateAsync({
				companyId,
				contactId: associatedContactId,
				timeZone: userTimeZone,
			});
			setMode('auto');
			onChange(result.url);
			onSchedulerEngaged?.();
			window.open(result.url, '_blank', 'noopener,noreferrer');
		} catch (err) {
			setErrorText('Could not load the demo scheduler link.');
			console.error('Demo scheduler link error:', err);
		}
	};

	const onManualClick = () => {
		setMode('manual');
		setErrorText(null);
		onChange('');
		onSchedulerDisengaged?.();
	};

	return (
		<div className={css(styles.schedulerRoot)}>
			{mode === 'manual' ? (
				<Button size='sm' onClick={onAutoClick} disabled={scheduleDemo.isPending}>
					{scheduleDemo.isPending ? 'Loading…' : 'Find Demo Spot'}
				</Button>
			) : (
				<Button size='sm' variant='secondary' onClick={onManualClick}>
					Enter Demo Time Manually
				</Button>
			)}
			{errorText ? <div className={css(styles.error)}>{errorText}</div> : null}
		</div>
	);
};

/* -------------------------------------------------------------------------- */
/*  Styles                                                                    */
/* -------------------------------------------------------------------------- */

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	label: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	labelText: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	requiredAsterisk: {
		// Smaller, accent-coloured asterisk — matches the legacy
		// `DealField/styles.ts` rule that selected the inner `<span>` with
		// `fontSize: '11px'` (the surrounding label is 14px). The asterisk is
		// purely decorative; the meaningful "required" comes from the input's
		// own semantics, so we set `aria-hidden` on the span at the call
		// site.
		marginLeft: 2,
		fontSize: 11,
		color: 'var(--accent)',
	},
	error: {
		fontSize: 11,
		color: 'var(--accent)',
	},
	unsupported: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		fontStyle: 'italic',
		padding: '8px 10px',
		background: 'var(--bg-elev)',
		border: '1px dashed var(--border)',
		borderRadius: 6,
	},
	richEditor: {
		minHeight: 120,
		border: '1px solid var(--border)',
		borderRadius: 8,
		background: 'var(--bg)',
	},
	dateTimeWrap: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	dateTimeRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	dateCol: {
		// `DateInput` renders a components-v2 `ButtonGroup` whose default
		// width is `fit-content`. Inside this row the parent flex column
		// must (a) take 2 of every 3 horizontal units (so the date input
		// has room for the YYYY-MM-DD text) and (b) force the inner
		// ButtonGroup to fill 100% — otherwise the date portion collapses
		// to ~icon-width and the rep sees only the calendar button.
		flex: 2,
		minWidth: 0,
		':nth-child(1n) > *': {
			width: '100%',
		},
	},
	timeCol: {
		// Time inputs are short — bound the column width so the date
		// portion gets the bulk of the row, and pin a sensible min so the
		// `--:-- ⌚` indicator never gets squeezed out.
		flex: '0 0 auto',
		width: 130,
	},
	tzHint: {
		fontSize: 11,
		color: 'var(--ink-muted)',
		whiteSpace: 'nowrap',
	},
	schedulerRoot: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
});
